import React, { useContext } from "react";
import AuthenticationService from "../services/AuthenticationService";
import Input from "../ui-components/Input";
import ConnectionCustomInput from "../ui-components/ConnectionCustomInput";
import checkIfOptionIsVisible from "../utils/checkIfOptionIsVisible";
import ConfiguratorContext from "./ConfiguratorContext";
import ConfiguratorSurveyBlockHeader from "./ConfiguratorSurveyBlockHeader";
import ConfiguratorSurveyBoolQuestion from "./ConfiguratorSurveyBoolQuestion";
import ServiceConfiguratorSelectQuestion from "./ServiceConfiguratorSelectQuestion";
import ConfiguratorSurveyKCCSelector from "./ConfiguratorSurveyKCCSelector";
import ConnectionCustomServiceConfiguratorSelectQuestion from "./ConnectionCustomServiceConfiguratorSelectQuestion";
import { CustomInputConnettoriGeometria } from "../feature/services/connettori";
import { CustomInputConnettoriRinforzo } from "../feature/services/connettori";
import { CustomInputRinforzoNodo } from "../feature/services/rinforzo-nodo";
import { CustomInputConnettoriCarichi } from "../feature/services/connettori";
import ConfiguratorSurveyKColorInput from "./ConfiguratorSurveyKColorInput";

const ConfiguratorSurveyQuestion = ({
  question,
  value,
  onChange,
  responses,
  role,
  disabled = false,
  canonicalRender = false,
}) => {
  const user = AuthenticationService.getUserData();
  const context = useContext(ConfiguratorContext);

  const {
    options,
    input_type,
    title,
    description,
    image_filename,
    required,
    hidden_question,
    button_select_question,
    metadata_,
  } = question;

  const hasCustomRender =
    !canonicalRender
    && ['number_input', 'select'].includes(input_type)
    && ['uom_question', 'symbol_question'].some(
      (k) => Object.keys(metadata_ || {}).includes(k) && metadata_[k] !== ""
    );

  let questionComponent = null;

  switch (input_type) {
    case "bool":
      questionComponent = (
        <ConfiguratorSurveyBoolQuestion
          value={value}
          onClick={(v) => onChange(v)}
          disabled={disabled}
          canonicalRender={canonicalRender}
        />
      );
      break;

    case "text_input":
      questionComponent = (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type="text"
          disabled={disabled}
        />
      );
      break;
    case "number_input":
      if (hasCustomRender) {
        questionComponent = (
          <ConnectionCustomInput
            label={description}
            unitOfMeasurement={metadata_.uom_question}
            symbol={metadata_.symbol_question}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            value={value}
            disabled={disabled}
          />
        );
      } else {
        questionComponent = (
          <Input
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            type="number"
            disabled={disabled}
          />
        );
      }
      break;

    case "select":
      if (options.length) {
        if (hasCustomRender) {
          questionComponent = (
            <ConnectionCustomServiceConfiguratorSelectQuestion
              label={description}
              unitOfMeasurement={metadata_.uom_question}
              symbol={metadata_.symbol_question}
              value={value}
              onChange={(value) => onChange(value, true)}
              options={options.filter((o) => {
                return canonicalRender || checkIfOptionIsVisible(
                  o,
                  {
                    ...context,
                    responses: responses || context.responses,
                  },
                  user,
                  value
                );
              })}
              disabled={disabled}
              input_type={input_type}
            />
          );
        } else {
          questionComponent = (
            <ServiceConfiguratorSelectQuestion
              value={value}
              onChange={(value) => onChange(value, true)}
              options={options.filter((o) => {
                return canonicalRender || checkIfOptionIsVisible(
                  o,
                  {
                    ...context,
                    responses: responses || context.responses,
                  },
                  user,
                  value
                );
              })}
              multi={false}
              selectWithButton={button_select_question}
              disabled={disabled}
              canonicalRender={canonicalRender}
            />
          );
        }
      }
      break;

    case "multi_select": {
      if (options.length) {
        questionComponent = (
          <ServiceConfiguratorSelectQuestion
            value={value}
            onChange={(value) => onChange(value, true)}
            options={options.filter((o) => {
              return canonicalRender || checkIfOptionIsVisible(o, {
                  ...context,
                  responses: responses || context.responses,
                }, user, value);
            })}
            multi={true}
            selectWithButton={button_select_question}
            disabled={disabled}
            canonicalRender={canonicalRender}
          />
        );
      }
      break;
    }

    case "kcc_color_input":
      questionComponent = (
        <ConfiguratorSurveyKCCSelector
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );
      break;

    case "connector_input":
      const props = {value, onChange, role};
      switch (metadata_?.type) {
        case "geometria":
          questionComponent = <CustomInputConnettoriGeometria {...props} />;
          break;
        case "rinforzo":
          questionComponent = <CustomInputConnettoriRinforzo {...props} />;
          break;
        case "carichi":
          questionComponent = <CustomInputConnettoriCarichi {...props} />;
          break;
        default:
          throw Error("Unhandled or missing input type (metadata_.type=" + metadata_?.type + ")");
      }
      break;
    case "k_color_input":
      questionComponent = (
        <ConfiguratorSurveyKColorInput
          value={value}
          onChange={onChange}
          disabled={disabled}
          question={question}
          responses={responses}
        />
      )
      break;
  case "node_reinforcement_input":
    questionComponent = (
      <CustomInputRinforzoNodo
        value={value}
        onChange={onChange}
      />
    );
    break;
  default:
      break;
  }

  if (!questionComponent) {
    return null;
  }

  const isHidden =
    hidden_question?.length &&
    hidden_question.indexOf(`role_type_${role}`) >= 0;

  const containerCls = ["w-full flex flex-col"];
  if (isHidden) {
    containerCls.push("hidden");
  }
  else if (!canonicalRender) {
    if (!hasCustomRender)
      containerCls.push("border-b border-am-700 mb-8 pb-4");
    else
      containerCls.push("mb-5");
  }

  return (
    <div
      className={containerCls.join(" ")}
    >
      {!canonicalRender && (
        <ConfiguratorSurveyBlockHeader
          title={title}
          description={hasCustomRender ? "" : description}
          image_filename={image_filename}
          required={required}
          type="question"
          hidden={isHidden}
        />
      )}
      {questionComponent}
    </div>
  );
};

export default ConfiguratorSurveyQuestion;
